<template>
	<div>
		<k-dialog v-model="dialog_open" persistent max-width="840px">
			<k-card>
				<k-card-title class="d-flex pb-4">
					<h3 v-if="collection_not_sharable">Manage Collection Editors</h3>
					<h3 v-else>Share{{is_course ? ' “My Content” from this':''}} collection with other users</h3>
					<k-spacer></k-spacer>
					<k-btn color="secondary" @click="$emit('cancel_share')"><k-icon small class="mr-2">fas fa-check</k-icon> Done</k-btn>
				</k-card-title>
				<k-card-text>
					<div class="d-flex">
						<k-btn v-if="collection_not_sharable" color="primary" class="k-tight-btn" @click="add_user"><k-icon small class="mr-2">fas fa-plus</k-icon>Add a new editor</k-btn>
						<k-btn v-else color="primary" class="k-tight-btn" @click="add_user"><k-icon small class="mr-2">fas fa-plus</k-icon>Share with a new user</k-btn>
						<k-btn color="teal" v-if="show_copy_email_list_button" dark class="k-tight-btn ml-2" @click="copy_email_list"><k-icon small class="mr-2">fas fa-copy</k-icon>Copy Email List</k-btn>
						<k-spacer/>
						<div v-if="!collection_not_sharable"><k-btn color="green darken-2" dark @click="show_subscription_code_info"><k-icon small class="mr-2">fas fa-share-nodes</k-icon> Show Subscription Code</k-btn></div>
						<!-- <div>
							<b style="font-size:16px" class="mr-2">Subscription Code:</b>
							<k-btn color="green darken-2" dark @click="copy_subscription_code_to_clipboard"><span style="font-size:18px">{{subscription_code}}</span><k-icon small class="ml-2">fas fa-copy</k-icon></k-btn>
							<k-btn class="ml-2" icon color="light-blue" @click="show_subscription_code_info"><k-icon large>fas fa-circle-info</k-icon></k-btn>
						</div> -->
					</div>
					<div v-if="rows.length==0 && collection_not_sharable" class="text-center mt-8 mb-4" style="font-size:18px"><i>No other editors currently specified.</i></div>
					<div v-if="rows.length==0 && !collection_not_sharable" class="text-center mt-8 mb-4" style="font-size:18px"><i>Not currently shared with any other users.</i></div>
					<k-data-table v-if="rows.length>0" light :headers="headers" :items="rows" :footer-props="{itemsPerPageOptions:[10,50,100,-1]}" :items-per-page="-1" >
						<template v-slot:item="{ item }">
							<tr>
								<td>
									<k-btn icon x-small class="mr-2" :accessible-label="`Edit sharing for ${item.first_name} ${item.last_name}`" @click="edit_user(item)">
										<k-icon>fas fa-edit</k-icon>
									</k-btn>
									<span>{{ item.last_name }}, {{ item.first_name }}, ({{ item.email }})</span>
								</td>
								<td class="d-flex justify-center">
									<k-icon v-if="item.has_edit_rights" small>fas fa-check</k-icon>
									<k-icon v-if="!item.has_edit_rights" small>fas fa-xmark</k-icon>
								</td>
							</tr>
						</template>
					</k-data-table>
				</k-card-text>
			</k-card>
		</k-dialog>
	</div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'

/*
This component allows designated users to manage who has access to a collection
It is used for the following situations:

- A "my" collection that the owner/creator of the collection wants to share with other users. here:
	- Any editor for the collection can view this component and use it to:
		- grant view or edit rights to any other users
		- revoke priviledges to other users
	- A subscription code can be used to allow others to "self-grant" access
		- Once a user has self-granted access, an editor for the collection can modify the user's rights using this component

- A "repo" collection that has been designated as "subscription_only". here:
	- Any editor(?) for the collection can view this component and use it to:
		- grant view or edit rights to any other users
		- revoke priviledges to other users
	- A subscription code can be used to allow others to "self-grant" access
		- Once a user has self-granted access, an editor for the collection can modify the user's rights using this component

TBD: should ANY editor be allow to do these things here??

- Any other collection:
	- A superuser, or a user who has subject-level edit rights to the subject for the collection, or the subdomain admin for subdomain-specific collections, can view this component and use it as follows:
		- grant edit rights to any other users
		- if the collection is *not* active, grant view rights to the collection (otherwise anyone can definitionally see it)
		- revoke priviledges to other users
	- Note that in this situation, the `collection_not_sharable` prop will be true; otherwise this prop will be false
*/

export default {
	// components: { },
	props: {
		collection: { type: Object, required: true },
		course_code: { type: String, required: true },
		// note that if collection_not_sharable is true, we only allow access to this component for *admins* of the collection -- those authorize to manage who has editorial access
		// and we also ensure in the associated services that only these users can manage who has editorial access
		collection_not_sharable: { type: Boolean, required: true },
	},
	data() {
		return {
			dialog_open: true,
			user_rights: [],
			headers: [
				{ text: 'User', align: 'left', sortable: true, value: 'user' },
				{ text: 'Collection Editor?', align: 'center', sortable: false, value: 'has_edit_rights' },
			],
			add_shared_dialog_open: false,
			edit_shared_dialog_open: false,
			user_being_edited: null,
		}
	},
	computed: {
		...mapState(['user_info']),
		...mapGetters([]),
		rows() {
			let arr = []
			for (let identifier in this.user_rights) {
				const user = this.user_rights[identifier]
				const o = {
					identifier: identifier,
					admin_rights_id: user.admin_rights_id,
					first_name: user.first_name,
					last_name: user.last_name,
					email: user.email,
					has_edit_rights: this.has_edit_rights(user.rights),
					rights_string: user.rights
				}
				arr.push(o)
			}
			return (arr)
		},
		course_or_repo() { return ['course','repo'].includes(this.collection.collection_type) },
		is_course() { return ['course'].includes(this.collection.collection_type) },
		is_repo() { return ['repo'].includes(this.collection.collection_type) },
		manage_view_access() {
			// if this.collection_not_sharable, 
			if (this.collection_not_sharable) {
				// we only offer to manage view access if the collection is currently inactive
				return (this.collection.active != 'yes')
			}

			return true
		},
		subscription_code() {
			// subscription code always starts with the lp's code...
			let s = this.collection.subscription_code

			// then if this is a course or repo, add the user's user_id, because for these types, subscriptions go to the user's added content for the collection
			if (this.is_course) {
				s += '-' + this.user_info.user_id
			}

			return s
		},
		show_copy_email_list_button() {
			return this.user_rights.length > 0
		},
		email_list() {
			// A list of email addresses for all users who have been granted access to this collection, returned as a comma separated string
			return this.user_rights.map(r => r.email).join(', ')
		},
	},
	watch: {},
	created() { },
	mounted() {
		this.get_rights_for_collection()
		if (!this.collection.subscription_code || this.collection.subscription_code === '') {
			this.generate_subscription_code()
		}
	},
	methods: {
		has_edit_rights(rights) {
			// SF: collection 'rights' are prefixed with either "lp" or "view_lp"
			return rights.startsWith("view") ? false : true
		},

		get_rights_for_collection() {
			const payload = {
				user_id: this.user_info.user_id,
				course_code: this.collection.course_code
			}
			U.ajax('get_rights_for_collection', payload, result => {
				this.user_rights = []
				for (let rights of result.user_rights) {
					this.user_rights.push(rights)
				}
			})
		},

		add_user() {
			let title = 'Share with new user'
			let text = 'Enter the email of the user you’d like to share with:'
			let acceptText = 'Share'
			let secondaryCheckbox = 'Grant collection edit rights to this user'
			if (this.collection_not_sharable) {
				if (this.manage_view_access) {
					title = 'Add new editor or viewer'
					text = 'Enter the email of the user you’d like to make a collection editor or viewer:'
					acceptText = 'Make Editor/Viewer'
				} else {
					title = 'Add new editor'
					text = 'Enter the email of the user you’d like to make a collection editor:'
					acceptText = 'Make Editor'
					secondaryCheckbox = null
				}
			}
			this.$prompt({
				title: title,
				text: text,
				promptType: 'autocomplete',
				serviceName: 'email_search',
				acceptText: acceptText,
				acceptIconAfter: 'fas fa-arrow-right',
				cancelIcon: 'fas fa-xmark',
				secondaryCheckbox: secondaryCheckbox,
				// secondaryCheckbox: (this.is_repo || !this.manage_view_access) ? null : 'Grant collection edit rights to this user',
				dialogMaxWidth: 500
			}).then((rv) => {
				// If we are in a repo or we're only managing edit access, that means there is no secondary checkbox, and the email is the only thing in rv
				if (this.is_repo || !this.manage_view_access) {
					let email = $.trim(rv).toLowerCase().replace(/^(\S+).*/, '$1')
					// if collection isn't sharable, we're *always* granting edit rights in this case;
					// otherwise if it's a repository, we're *never* granting edit rights
					let grant_edit_rights = (this.collection_not_sharable) ? true : false
					this.share_collection(email, grant_edit_rights)
				} else {
					let grant_edit_rights = rv[1]
					let email = $.trim(rv[0]).toLowerCase().replace(/^(\S+).*/, '$1')
					this.share_collection(email, grant_edit_rights)
				}
			}).catch(n=>{console.log(n)}).finally(f=>{})
		},

		share_collection(email, grant_edit_rights) {
			this.add_shared_dialog_open = false
			if (empty(email) || email.indexOf('@') == -1) {
				this.$alert('Please enter a valid user email.')
				return
			}
			for (let i = 0; i < this.user_rights.length; i++) {
				if (this.user_rights[i].email.toLowerCase() === email.toLowerCase()) {
					if (this.collection_not_sharable) {
						if (this.manage_view_access) this.$alert('This user is already an editor or viewer.')
						else this.$alert('This user is already an editor.')
					} else {
						this.$alert('You have already shared to this email.')
					}
					return
				}
			}
			const payload = {
				user_id: this.user_info.user_id,
				course_code: this.course_code,
				recipient_email: email,
				grant_edit_rights: grant_edit_rights,
				lp_id: this.collection.lp_id
			}
			this.$store.dispatch('share_learning_progression', payload).then((result) => {
				this.$inform(sr('Collection has been shared with $1', email))
			}).catch(n => { console.log(n) }).finally(f => {
				this.$nextTick(() => {
					this.get_rights_for_collection()
				})
			});
		},

		edit_user(user) {
			let cancelText = 'Remove User From Collection'
			let hideAccept = false
			if (this.collection_not_sharable) {
				if (this.manage_view_access) {
					cancelText = 'Revoke user access'
				} else {
					cancelText = 'Revoke editor access'
					hideAccept = true
				}
			}
			this.$confirm({
				title: 'Edit user collection rights',
				text: `${user.last_name}, ${user.first_name} (${user.email})`,
				// hide remove accept text and icon if in repo or !manage_view_access, because all we can do here is remove the user
				hideAccept: hideAccept,
				acceptText: 'Save Changes',
				acceptIcon: 'fas fa-save',
				extraBtnText: 'Cancel',
				extraBtnIcon: 'fas fa-xmark',
				extraBtnColor: 'secondary',
				cancelText: cancelText,
				cancelIcon: 'fas fa-trash-alt',
				cancelColor: 'red darken-2',
				secondaryCheckbox: (hideAccept) ? null : 'Grant collection edit rights to this user',
				secondaryCheckboxInitiallyOn: user.has_edit_rights,
				dialogMaxWidth: 640,
			}).then((rv) => {
				// If hideAccept is true, that means there is no secondary checkbox, and we will just return
				if (hideAccept) return

				// if rv[0] is 'extra', it means that they clicked the cancel btn
				if (rv[0] == 'extra') return

				// if we're here, save rights if they changed
				let grant_edit_rights = rv[1]
				if (grant_edit_rights != user.has_edit_rights) {
					this.save_edit(user, grant_edit_rights)
				}
				return
			}).catch(n=>{
				this.remove_collection_user(user)
			}).finally(f=>{})
		},

		save_edit(user, grant_edit_rights) {
			this.edit_shared_dialog_open = false
			const payload = {
				user_id: this.user_info.user_id,
				course_code: this.collection.course_code,
				admin_rights_id: user.admin_rights_id,
				grant_edit_rights: grant_edit_rights,
				recipient_email: user.email,
				rights_string: user.rights_string
			}
			this.$store.dispatch('edit_user_shared_collection_rights', payload).then((result) => {
				this.$inform(sr('Rights for $1 updated', user.email))
			}).catch(n => console.log(n)).finally(f => {
				this.$nextTick(() => {
					this.get_rights_for_collection()
				})
			})
		},
		remove_collection_user(user) {
			this.edit_shared_dialog_open = false
			const payload = {
				user_id: this.user_info.user_id,
				admin_rights_id: user.admin_rights_id,
				recipient_email: user.email,
				rights_string: user.rights_string
			}
			this.$store.dispatch('remove_user_from_shared_collection', payload).then((result) => {
				this.$inform(sr('$1 has been removed as a shared user', user.email))
			}).catch(n => { console.log(n) }).finally(f => {
				this.$nextTick(() => {
					this.get_rights_for_collection()
				})
			});
		},

		generate_subscription_code() {
			// dispatch generate_subscription_code, which will generate a new code, save it in the db, then update the store (the updated code will then filter down to us here.)
			this.$store.dispatch('generate_subscription_code', this.collection.lp_id)
		},

		copy_subscription_code_to_clipboard() {
			U.copy_to_clipboard(this.subscription_code)
			this.$inform('Subscription code copied to clipboard')
		},

		show_subscription_code_info() {
			let msg
			if (this.collection.collection_type == 'my') {
				msg = `Instead of (or in addition to) choosing users to share with here, you can also give the following “Subscription Code” to other users:<div class="my-3 text-center" style="font-size:30px"><b>${this.subscription_code}</b></div>They can then get access to the collection by clicking the “SUBSCRIBE to a Content Collection” button from the <nobr>“<i class="fas fa-cubes-stacked"></i> My Content Collections”</nobr> area and entering the Subscription Code.`

			} else if (this.collection.collection_type == 'repo') {
				msg = `Instead of (or in addition to) choosing users to share with here, you can also give the following “Subscription Code” to other users:<div class="my-3 text-center" style="font-size:30px"><b>${this.subscription_code}</b></div>They can then get access to the collection by clicking the “SUBSCRIBE to another Repository” button from the <nobr>“<i class="fas fa-diagram-project"></i> ${this.$store.state.site_config.resourcerepos_noun}”</nobr> area and entering the Subscription Code.`

			} else {	// sharing 'my content' from a collection
				msg = `Instead of (or in addition to) choosing users to share with here, you can also give the following “Subscription Code” to other users:<div class="my-3 text-center" style="font-size:30px"><b>${this.subscription_code}</b></div>They can then get access to your content by clicking the “SUBSCRIBE to a Content Collection” button from the <nobr>“<i class="fas fa-cubes-stacked"></i> My Content Collections”</nobr> area and entering the Subscription Code.`
			}

			this.$confirm({
				title: 'Subscription Code',
				text: msg,
				dialogMaxWidth: 600,
				acceptIcon: 'fas fa-check',
				cancelText: 'Copy Subscription Code',
				cancelIcon: 'fas fa-copy',
				cancelColor: 'green darken-2',
			}).then(y=>{}).catch(n=>{
				this.copy_subscription_code_to_clipboard()
			})
		},
		copy_email_list() {
			U.copy_to_clipboard(this.email_list)
			this.$inform('Email list copied to clipboard')
		},
	}
}
</script>

<style lang="scss">
</style>
